import {AfterContentInit, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Validators}                                                from '@angular/forms';
import {
  ApDynformsConfigFieldset
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {
  ApDynformsConfigTextbox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-textbox';
import {ApDynformsComponent}                                       from '../../ap-dynforms/ap-dynforms.component';
import {ApDynformsValidator}                                       from '../../ap-dynforms/ap-dynforms-validator';
import {DriverStore}                                               from '../../stores/docu/driver.store';
import {FormStore}                                                 from '../../stores/layout/form.store';
import {LoginStore}                                                from '../../stores/login/login.store';
import {BehaviorSubject, combineLatest, Observable, Subscription}  from 'rxjs';
import {
  ApDynformsConfigCheckbox
}                                                                  from '../../ap-dynforms/config/ap-dynforms-config-checkbox';
import {ApDateService}                                             from '../../ap-core/services/ap-date-service';
import {DriverEditFormData}                                        from './types/ap-drivers-edit.types';
import {
  ResourcesDataService
}                                                                  from '../../services/data/resources/resources-data.service';
import {nameOf}                                                    from '../../ap-core/utils/ap-name-of';
import {map}                                                       from 'rxjs/operators';
import {MachineStore}                                              from '../../stores/docu/machine.store';
import IDriver = Data.DocuContext.Driver.IDriver;

/**
 * Drivers edit component.
 */
@Component({
  selector: 'ap-drivers-edit',
  template: `
    <ap-dynforms [fieldsets]="formConfig" [caption]="caption" [loading$]="loading$">
      <div class="ap-form-actions" dynforms.action>
        <button id="button_drivers_edit_cancel"
                type="button"
                class="k-button k-primary ap-form-button-left"
                (click)="onCancelClick()">{{ 'Global__Cancel' | translate }}
        </button>
        <button id="button_drivers_edit_save"
                type="button"
                class="k-button k-primary ap-form-button-right"
                [disabled]="(isSaveDisabled$ | async)"
                (click)="onSubmitClick()">{{ 'Global__Save' | translate }}
        </button>
      </div>
    </ap-dynforms>`
})
export class ApDriversEditComponent implements OnInit, AfterContentInit, OnDestroy {
  @ViewChild(ApDynformsComponent, {static: true}) dynForm: ApDynformsComponent;
  public data: IDriver | undefined;
  public caption: string;
  public formConfig: ApDynformsConfigFieldset[];
  public isNewMode = false;
  public loading$: Observable<boolean>;
  public isSaveDisabled$ = new BehaviorSubject<boolean>(true);

  private _formData: DriverEditFormData | undefined;
  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private loginStore: LoginStore,
              private driversStore: DriverStore,
              private machineStore: MachineStore,
              private dateService: ApDateService,
              private resourcesDataService: ResourcesDataService) {
  }

  /**
   * when the component is loaded
   */
  public ngOnInit(): void {
    this.loading$ = combineLatest([
      this.machineStore.Loading$,
      this.driversStore.Listen(s => s.saving)
    ]).pipe(
      map(loadings => loadings.some(loading => loading))
    );
    this.isNewMode = !this.data;
    this._formData = {
      FirstName: this.data?.Firstname,
      LastName: this.data?.Name,
      IsActive: this.resourcesDataService.isResourceActive(this.data)
    };
    this.caption = this.isNewMode ? 'Settings__Lbl_Worker_Create' : 'Settings__Lbl_Worker_Edit';
    this.formBuilder();
  }

  public ngAfterContentInit(): void {
    this._subscriptions.push(
      this.driversStore.Listen(s => s.saved).subscribe(saved => {
        if (saved) {
          this.formStore.closeForm();
        }
      })
    );
    this._subscriptions.push(
      this.dynForm.FormValues$.subscribe(() => {
        this.dynForm.form?.updateValueAndValidity();
        const formValues = this.dynForm.form?.getRawValue() as DriverEditFormData;
        const isSaveAvailable = JSON.stringify(formValues) !== JSON.stringify(this._formData)
          && this.dynForm.form?.valid;
        this.isSaveDisabled$.next(!isSaveAvailable);
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(x => x?.unsubscribe());
  }

  /**
   * Submitting the changes.
   * Either creating a new object or updating the current.
   */
  public onSubmitClick(): void {
    const driver = this.data ?? ({} as IDriver);
    const formData = this.dynForm.form.getRawValue() as DriverEditFormData;
    if (!formData) {
      return;
    }
    driver.FarmId = this.loginStore.SelectedFarmId;
    driver.Firstname = formData.FirstName;
    driver.Name = formData.LastName;
    driver.IsDeleted = !formData.IsActive;
    driver.DeletedAt = formData.IsActive ? undefined : this.dateService.getUtc();
    driver.DeletedBy = formData.IsActive ? undefined : this.loginStore.User.Id;
    if (this.isNewMode) {
      this.driversStore.createDriver(driver);
    } else {
      this.driversStore.updateDriver(driver);
    }
  }

  /**
   * Cancel a clicked eventHandler - closes form.
   */
  public onCancelClick(): void {
    this.formStore.closeForm();
  }

  /**
   * binding of validators
   */
  private formBuilder(): void {
    this.formConfig = [
      new ApDynformsConfigFieldset({
        key: 'driver',
        config: [
          new ApDynformsConfigTextbox({
            key: nameOf<DriverEditFormData>('FirstName'),
            label: 'Docu_Ini__Forename',
            value: this._formData.FirstName,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigTextbox({
            key: nameOf<DriverEditFormData>('LastName'),
            label: 'Docu_Ini__Surname',
            value: this._formData.LastName,
            validators: [
              new ApDynformsValidator({
                validator: Validators.required,
                errorKey: 'Settings__Msg_Vali_Value_Required'
              })
            ]
          }),
          new ApDynformsConfigCheckbox({
            key: nameOf<DriverEditFormData>('IsActive'),
            label: 'Global__Active',
            value: this._formData.IsActive,
            disabled$: this.machineStore.Machines$.pipe(
              map(machines => machines.some(x => !!x.DefaultDriver && x.DefaultDriver?.Id === this.data?.Id))
            )
          })
        ]
      })
    ];
  }
}
